import { useMemo, useState } from 'react';
import {
  EditOutlined,
  FileExcelOutlined,
  InboxOutlined,
  PlusOutlined,
  RollbackOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Popconfirm, Segmented, Table, Tooltip, message } from 'antd';
import JobDescriptionList from '@/components/job-positions/JobDescriptionList';
import JobPositionDrawer from '@/components/job-positions/JobPositionDrawer';
import JobPositionImportModal from '@/components/job-positions/JobPositionImportModal';
import JobPositionMatchingModal from '@/components/job-positions/JobPositionMatchingModal';
import useJobPositions, {
  JOB_POSITIONS_QUERY_KEY,
  jobPositionLevelShort,
  useJobPositionsT,
} from '@/components/job-positions/useJobPositions';
import {
  archiveJobPosition,
  restoreJobPosition,
} from '@/services/jobPositions.service';
import { QUERY_KEY } from '@/shared/constants';

const fold = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd');

const small = '!h-7 !rounded-[50px] !px-3 !text-xs';

/**
 * "Job positions" tile of the company profile: the company's catalog
 * (names SR/EN, required education level, job description), added by hand
 * or imported from the client's Excel, and the one-off matching of
 * employees' typed positions to it. The organisational sector of a position
 * is the client's wording, not a platform sector.
 */
export default function CompanyJobPositionsPanel({ companyId }) {
  const { t, tj } = useJobPositionsT();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('active');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [matchOpen, setMatchOpen] = useState(false);

  const { data, isLoading } = useJobPositions(companyId, status);
  // The matching dialog always offers the active ones.
  const { data: active } = useJobPositions(companyId, 'active');
  const canManage = !!data?.canManage;

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: [JOB_POSITIONS_QUERY_KEY, companyId] });

  const toggle = useMutation({
    mutationFn: (p) => (p.status === 'archived' ? restoreJobPosition(p._id) : archiveJobPosition(p._id)),
    onSuccess: () => {
      message.success(tj('saved'));
      refresh();
    },
    onError: (error) => message.error(error?.response?.data?.message || tj('saveFailed')),
  });

  const rows = useMemo(() => {
    const items = data?.items || [];
    if (!search) return items;
    const q = fold(search);
    return items.filter((p) =>
      [p.nameSr, p.nameEn, p.sectorName, p.supervisorFunction, p.abCode].some((v) => fold(v).includes(q))
    );
  }, [data, search]);

  const columns = [
    {
      title: tj('nameSr'),
      key: 'name',
      sorter: (a, b) => a.nameSr.localeCompare(b.nameSr, 'sr'),
      render: (_, p) => (
        <div>
          <div className="font-semibold">{p.nameSr}</div>
          {p.nameEn && <div className="text-xs text-zinc-400">{p.nameEn}</div>}
        </div>
      ),
    },
    {
      title: tj('sectorName'),
      dataIndex: 'sectorName',
      key: 'sectorName',
      width: 200,
      sorter: (a, b) => (a.sectorName || '').localeCompare(b.sectorName || '', 'sr'),
      render: (text) => text || '—',
    },
    {
      title: tj('levelShort'),
      key: 'level',
      width: 90,
      render: (_, p) => jobPositionLevelShort(p) || '—',
    },
    {
      title: (
        <Tooltip title={tj('headcountHint')}>{tj('headcountShort')}</Tooltip>
      ),
      key: 'headcount',
      width: 110,
      render: (_, p) => `${p.employeesCount || 0} / ${p.headcount ?? '—'}`,
    },
    {
      title: tj('supervisorFunction'),
      dataIndex: 'supervisorFunction',
      key: 'supervisorFunction',
      width: 200,
      render: (text) => text || '—',
    },
    canManage && {
      key: 'actions',
      width: 90,
      render: (_, p) => (
        <div className="flex gap-1">
          <Tooltip title={t('edit', { ns: 'forms' })}>
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => setEditing(p)} />
          </Tooltip>
          <Popconfirm
            title={p.status === 'archived' ? tj('restoreConfirm') : tj('archiveConfirm')}
            description={p.status !== 'archived' && p.employeesCount ? tj('archiveHolders', { count: p.employeesCount }) : null}
            onConfirm={() => toggle.mutate(p)}
          >
            <Tooltip title={p.status === 'archived' ? tj('restore') : tj('archive')}>
              <Button
                size="small"
                type="text"
                icon={p.status === 'archived' ? <RollbackOutlined /> : <InboxOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ].filter(Boolean);

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          size="small"
          value={status}
          onChange={setStatus}
          options={[
            { value: 'active', label: tj('statusActive') },
            { value: 'archived', label: tj('statusArchived') },
          ]}
        />
        <Input.Search
          allowClear
          size="small"
          className="!w-64"
          placeholder={tj('search')}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex-1" />
        {canManage && (
          <>
            <Button className={small} icon={<TeamOutlined />} disabled={!active?.count} onClick={() => setMatchOpen(true)}>
              {tj('matchButton')}
            </Button>
            <Button className={small} icon={<FileExcelOutlined />} onClick={() => setImportOpen(true)}>
              {tj('importButton')}
            </Button>
            <Button type="primary" className={small} icon={<PlusOutlined />} onClick={() => setEditing({})}>
              {tj('addButton')}
            </Button>
          </>
        )}
      </div>

      <p className="m-0 text-[12.5px] text-zinc-500">{tj('panelHint')}</p>

      <Table
        size="small"
        rowKey="_id"
        loading={isLoading}
        dataSource={rows}
        columns={columns}
        pagination={{ pageSize: 50, showSizeChanger: false, hideOnSinglePage: true }}
        locale={{ emptyText: tj(status === 'active' ? 'empty' : 'emptyArchived') }}
        expandable={{
          rowExpandable: (p) => !!p.description,
          expandedRowRender: (p) => (
            <JobDescriptionList text={p.description} className="text-[13px] leading-relaxed text-zinc-700" />
          ),
        }}
      />

      {canManage && (
        <>
          <JobPositionDrawer
            companyId={companyId}
            position={editing}
            onClose={() => setEditing(null)}
            onSaved={refresh}
          />
          <JobPositionImportModal
            companyId={companyId}
            open={importOpen}
            onClose={() => setImportOpen(false)}
            onImported={refresh}
          />
          <JobPositionMatchingModal
            companyId={companyId}
            open={matchOpen}
            onClose={() => setMatchOpen(false)}
            positions={active?.items}
            onAssigned={() => {
              refresh();
              queryClient.invalidateQueries({ queryKey: [QUERY_KEY.employees] });
              queryClient.invalidateQueries({ queryKey: [QUERY_KEY.employee] });
            }}
          />
        </>
      )}
    </div>
  );
}
